import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

// Internal type for UI representation
type UIPositionValue =
	| 'bottom-left'
	| 'bottom-center'
	| 'bottom-right'
	| 'middle-left'
	| 'middle-right'

type PickerPositionValue =
	| 'bottom-left'
	| 'bottom-center'
	| 'bottom-right'
	| 'middle-left'
	| 'middle-right'

interface PositionPickerProps {
	value: PickerPositionValue
	onChange: (value: PickerPositionValue) => void
	label?: string
}

// Internal type for the positions array
type Position = {
	value: UIPositionValue // Use internal UI type
	label: string
}

// Map UI position to canonical position
const mapUIToCanonical = (
	uiPosition: UIPositionValue
): PickerPositionValue => {
	return uiPosition
}

const positions: Position[] = [
	{
		value: 'middle-left',
		label: 'Middle Left',
	},
	{
		value: 'middle-right',
		label: 'Middle Right',
	},
	{
		value: 'bottom-left',
		label: 'Bottom Left',
	},
	{
		value: 'bottom-center',
		label: 'Bottom Center',
	},
	{
		value: 'bottom-right',
		label: 'Bottom Right',
	},
]

const getPreviewButtonPosition = (position: UIPositionValue) => {
	switch (position) {
		case 'middle-left':
			return {
				left: '14px',
				top: '50%',
				transform: 'translateY(-50%)',
				borderRadius: '0 12px 12px 0',
			}
		case 'middle-right':
			return {
				right: '14px',
				top: '50%',
				transform: 'translateY(-50%)',
				borderRadius: '12px 0 0 12px',
			}
		case 'bottom-left':
			return {
				left: '14px',
				bottom: '14px',
				borderRadius: '9999px',
			}
		case 'bottom-center':
			return {
				left: '50%',
				bottom: '0px',
				transform: 'translateX(-50%)',
				borderRadius: '12px 12px 0 0',
			}
		default:
			return {
				right: '14px',
				bottom: '14px',
				borderRadius: '9999px',
			}
	}
}

export function PositionPicker({
	value,
	onChange,
	label = 'Position',
}: PositionPickerProps) {
	return (
		<div className="space-y-2">
			
			<div className="relative max-w-96 ">
				<div className="border-2 border-gray-200 rounded-lg overflow-hidden">
					<div className="bg-gray-100 p-2 border-b border-gray-200">
						<div className="flex items-center gap-1.5">
							<div className="w-3 h-3 rounded-full bg-gray-300" />
							<div className="w-3 h-3 rounded-full bg-gray-300" />
							<div className="w-3 h-3 rounded-full bg-gray-300" />
						</div>
					</div>
					<div className="aspect-video bg-gradient-to-b from-white to-gray-50 p-4 relative">
						<div className="absolute inset-4 rounded-md border border-dashed border-gray-200" />
						{positions.map((position) => {
							const isSelected = value === mapUIToCanonical(position.value)
							return (
								<button
									type="button"
									key={position.value}
									onClick={() => onChange(mapUIToCanonical(position.value))}
									className={cn(
										'absolute h-8 px-3 border transition-all shadow-sm',
										'flex items-center justify-center',
										position.value === 'bottom-center' && '!border-0 shadow-none',
										isSelected
											? 'bg-blue-600 border-blue-600 text-white shadow-md'
											: 'bg-white border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
									)}
									style={getPreviewButtonPosition(position.value)}>
									<div
										className={cn(
											'w-1.5 h-1.5 rounded-full mr-1.5',
											isSelected ? 'bg-white' : 'bg-blue-500'
										)}
									/>
									<span className="text-[11px] font-medium leading-none">Widget</span>
									<span className="sr-only">{position.label}</span>
								</button>
							)
						})}
					</div>
				</div>
			</div>
		</div>
	)
}
